import CustomFormSubmissionStatusChip from "@/CustomForms/CustomFormSubmissionStatusChip.vue";

// Shared by the tenant-wide Forms & Checklists Completion report and the
// customer record's Forms tab, which render the same FormsAndChecklistsDatatable
// and so answer to the same `data` keys. Duplicating the list let the two drift.
//
// `data` keys are the contract with FormsAndChecklistsDatatable#view_columns — a
// mismatch renders an empty column rather than failing loudly. The two progress
// columns are computed in Ruby, so there is no SQL for the server to sort or
// search them by; status is an integer-backed enum, which a text search would
// match on the integer rather than the label.
//
// `filterName` binds a filter to its column header. DatatableV3 renders a filter
// in the header only when its name matches `filterName || data`, and pushes every
// unmatched filter into the bar above the table — so without these the filters
// stack above the grid while their columns show a redundant generic search box.
// The corollary is that a column whose `filterName` has no filter behind it gets
// that generic box, which is why the omit options below exist.
type SubmissionColumnOptions = {
  // Redundant on a customer record, where every row is that customer's.
  includeCustomer?: boolean;
  // Offered as a filter only on the tenant report.
  includeDepartment?: boolean;
};

export function submissionColumns({
  includeCustomer = true,
  includeDepartment = true
}: SubmissionColumnOptions = {}) {
  return [
    {
      data: "attached_on",
      title: "Attached On",
      filterName: "attached_date",
      searchable: false
    },
    { data: "job_id", title: "Job" },
    // Two copies of one template on a job are legitimate — one per visit — and
    // without the anchor they differ only by Attached On. No filterName: there is no
    // by-appointment filter behind it, and naming one would render a generic search
    // box in the header that filters nothing.
    { data: "appointment_id", title: "Appointment", searchable: false },
    {
      data: "form",
      title: "Form",
      filterName: "custom_form_template_id",
      className: "wrap-when-responsive"
    },
    ...(includeCustomer
      ? [
          {
            data: "customer",
            title: "Customer",
            className: "wrap-when-responsive"
          }
        ]
      : []),
    ...(includeDepartment
      ? [
          {
            data: "department",
            title: "Department",
            filterName: "department_id"
          }
        ]
      : []),
    {
      data: "status",
      title: "Status",
      filterName: "custom_form_submission_status",
      searchable: false
    },
    {
      data: "required_for_close",
      title: "Required to Close",
      searchable: false
    },
    {
      data: "required_progress",
      title: "Required Progress",
      searchable: false,
      orderable: false
    },
    {
      data: "overall_progress",
      title: "Overall Progress",
      searchable: false,
      orderable: false
    },
    {
      data: "submitted_at",
      title: "Submitted On",
      filterName: "submitted_date",
      searchable: false
    },
    // "Submitted By", not "Tech": a submission has no technician FK, and the
    // job's owner sold the work rather than filled the form in. See
    // FormsAndChecklistsReport's class comment.
    {
      data: "submitted_by",
      title: "Submitted By",
      filterName: "submitted_by",
      searchable: false,
      orderable: false
    }
  ];
}

export const submissionStatusChipComponent = {
  rootId: "custom-form-submission-status-chip-vue",
  component: CustomFormSubmissionStatusChip
};
